<script setup lang="ts">
import { ref, onMounted, inject, Ref } from 'vue'
import { useNotifications } from '@/services/Notifications'
import { EmailTemplate } from '@/types/Notifications'
import { Member } from '@/types/Member'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { EllipsisVerticalIcon, PlusCircleIcon } from '@heroicons/vue/24/solid'
import { Mail, Bell, Clock, CheckCircle2, XCircle } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

const { templates, rules, logs, loading, error, getEmailTemplates, saveEmailTemplate, deleteEmailTemplate, getNotificationRules, getNotificationLogs } = useNotifications()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const selectedTab = ref('templates')
const showEditDialog = ref(false)
const editingTemplate = ref<Partial<EmailTemplate>>({
	name: '',
	subject: '',
	body: '',
	trigger: '',
	enabled: true,
})

const openNewTemplate = () => {
	editingTemplate.value = {
		name: '',
		subject: '',
		body: '',
		trigger: '',
		enabled: true,
	}
	showEditDialog.value = true
}

const openEditTemplate = (template: EmailTemplate) => {
	editingTemplate.value = { ...template }
	showEditDialog.value = true
}

const handleSaveTemplate = async () => {
	if (!vhostId || !vhostId.value) {
		return
	}

	const success = await saveEmailTemplate({
		...editingTemplate.value,
		vhost: parseInt(vhostId.value),
	})

	if (success) {
		toast('Template Saved', { description: `"${editingTemplate.value.name}" has been saved.` })
		showEditDialog.value = false
		await getEmailTemplates(vhostId.value)
	} else {
		toast('Error', { description: 'Failed to save template.' })
	}
}

const handleDeleteTemplate = async (template: EmailTemplate) => {
	const success = await deleteEmailTemplate(template.id)
	if (success && vhostId && vhostId.value) {
		toast('Template Deleted', { description: `"${template.name}" has been removed.` })
		await getEmailTemplates(vhostId.value)
	}
}

onMounted(async () => {
	if (vhostId && vhostId.value) {
		await Promise.all([
			getEmailTemplates(vhostId.value),
			getNotificationRules(vhostId.value),
			getNotificationLogs(vhostId.value),
		])
	}
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="grid gap-4 md:grid-cols-3">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Email Templates</CardTitle>
					<Mail class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ templates.length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Active Rules</CardTitle>
					<Bell class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ rules.filter((r) => r.enabled).length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Recent Notifications</CardTitle>
					<Clock class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ logs.length }}</div>
				</CardContent>
			</Card>
		</div>

		<Tabs v-model="selectedTab" class="space-y-4">
			<TabsList>
				<TabsTrigger value="templates">Email Templates</TabsTrigger>
				<TabsTrigger value="rules">Notification Rules</TabsTrigger>
				<TabsTrigger value="logs">Notification Logs</TabsTrigger>
			</TabsList>

			<TabsContent value="templates">
				<Card>
					<CardHeader>
						<div class="flex justify-between items-center">
							<div>
								<CardTitle class="text-lg font-normal">Email Templates</CardTitle>
								<CardDescription>Manage email templates for notifications</CardDescription>
							</div>
							<Button @click="openNewTemplate">
								<PlusCircleIcon class="h-4 w-4 mr-2" />
								New Template
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Subject</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Trigger</TableHead>
									<TableHead>Status</TableHead>
									<TableHead class="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<template v-if="templates.length > 0">
									<TableRow v-for="template in templates" :key="template.id">
										<TableCell class="font-medium">{{ template.name }}</TableCell>
										<TableCell>{{ template.subject }}</TableCell>
										<TableCell>
											<Badge :variant="template.type === 'system' ? 'secondary' : 'outline'">{{ template.type }}</Badge>
										</TableCell>
										<TableCell class="text-sm text-muted-foreground">{{ template.trigger || '-' }}</TableCell>
										<TableCell>
											<Badge :variant="template.enabled ? 'default' : 'secondary'">
												{{ template.enabled ? 'Active' : 'Disabled' }}
											</Badge>
										</TableCell>
										<TableCell class="text-right">
											<DropdownMenu>
												<DropdownMenuTrigger as-child>
													<Button variant="ghost" size="sm">
														<EllipsisVerticalIcon class="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent>
													<DropdownMenuItem @click="openEditTemplate(template)">Edit</DropdownMenuItem>
													<DropdownMenuItem>Duplicate</DropdownMenuItem>
													<DropdownMenuItem>Send Test</DropdownMenuItem>
													<DropdownMenuItem class="text-destructive" @click="handleDeleteTemplate(template)">Delete</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</TableCell>
									</TableRow>
								</template>
								<TableRow v-else>
									<TableCell colspan="6" class="h-24 text-center text-muted-foreground">
										<div v-if="loading">Loading templates...</div>
										<div v-else>No email templates found.</div>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="rules">
				<Card>
					<CardHeader>
						<div class="flex justify-between items-center">
							<div>
								<CardTitle class="text-lg font-normal">Notification Rules</CardTitle>
								<CardDescription>Configure when and how notifications are sent</CardDescription>
							</div>
							<Button>
								<PlusCircleIcon class="h-4 w-4 mr-2" />
								Add Rule
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Event</TableHead>
									<TableHead>Channel</TableHead>
									<TableHead>Recipients</TableHead>
									<TableHead>Status</TableHead>
									<TableHead class="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<template v-if="rules.length > 0">
									<TableRow v-for="rule in rules" :key="rule.id">
										<TableCell class="font-medium">{{ rule.name }}</TableCell>
										<TableCell>{{ rule.event }}</TableCell>
										<TableCell>{{ rule.channel || 'All' }}</TableCell>
										<TableCell>{{ rule.recipients.length }} recipients</TableCell>
										<TableCell>
											<Badge :variant="rule.enabled ? 'default' : 'secondary'">
												{{ rule.enabled ? 'Active' : 'Disabled' }}
											</Badge>
										</TableCell>
										<TableCell class="text-right">
											<Button variant="ghost" size="sm">Edit</Button>
										</TableCell>
									</TableRow>
								</template>
								<TableRow v-else>
									<TableCell colspan="6" class="h-24 text-center text-muted-foreground">
										No notification rules configured.
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>

			<TabsContent value="logs">
				<Card>
					<CardHeader>
						<CardTitle class="text-lg font-normal">Notification Logs</CardTitle>
						<CardDescription>History of sent notifications</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Template</TableHead>
									<TableHead>Recipient</TableHead>
									<TableHead>Subject</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Sent</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								<template v-if="logs.length > 0">
									<TableRow v-for="log in logs" :key="log.id">
										<TableCell class="font-medium">{{ log.templateName }}</TableCell>
										<TableCell>{{ log.recipient }}</TableCell>
										<TableCell>{{ log.subject }}</TableCell>
										<TableCell>
											<Badge :variant="log.status === 'sent' ? 'default' : log.status === 'failed' ? 'destructive' : 'secondary'">
												<component :is="log.status === 'sent' ? CheckCircle2 : log.status === 'failed' ? XCircle : Clock" class="h-3 w-3 mr-1" />
												{{ log.status }}
											</Badge>
										</TableCell>
										<TableCell class="text-sm text-muted-foreground">{{ log.sentDate }}</TableCell>
									</TableRow>
								</template>
								<TableRow v-else>
									<TableCell colspan="5" class="h-24 text-center text-muted-foreground">
										No notification logs available.
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			</TabsContent>
		</Tabs>

		<Dialog v-model:open="showEditDialog">
			<DialogContent class="sm:max-w-[600px]">
				<DialogHeader>
					<DialogTitle>{{ editingTemplate.id ? 'Edit' : 'Create' }} Email Template</DialogTitle>
					<DialogDescription>Configure the email template content and settings</DialogDescription>
				</DialogHeader>
				<div class="space-y-4 py-4">
					<div class="space-y-2">
						<Label for="templateName">Name</Label>
						<Input id="templateName" v-model="editingTemplate.name" placeholder="Template name" />
					</div>
					<div class="space-y-2">
						<Label for="templateSubject">Subject</Label>
						<Input id="templateSubject" v-model="editingTemplate.subject" placeholder="Email subject line" />
					</div>
					<div class="space-y-2">
						<Label for="templateTrigger">Trigger Event</Label>
						<Input id="templateTrigger" v-model="editingTemplate.trigger" placeholder="e.g., media.uploaded, user.registered" />
					</div>
					<div class="space-y-2">
						<Label for="templateBody">Body (HTML)</Label>
						<Textarea id="templateBody" v-model="editingTemplate.body" rows="8" class="font-mono text-sm" placeholder="<h1>Hello {{username}}</h1>" />
					</div>
					<div class="flex items-center space-x-2">
						<Switch id="templateEnabled" v-model:checked="editingTemplate.enabled" />
						<Label for="templateEnabled">Enabled</Label>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" @click="showEditDialog = false">Cancel</Button>
					<Button @click="handleSaveTemplate" :disabled="!editingTemplate.name || !editingTemplate.subject">Save</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>
</template>
